import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import { DefaultRouteModel } from "../../domain/models/DefaultRouteModel";
import useCrudForm from "../../../../../hooks/useCrudForm";
import { defaultRouteFormSchema } from "../../domain/schema/defaultRouteFormSchema";
import { DefaultRouteMutateFormTemplate } from "../features/DefaultRouteMutateFormTemplate";
import { Breadcrumb } from "../../../../general/modules/commons/breadcrumb/breadcrumb";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderTitle,
} from "../../../../general/modules/commons/layout/layout";
import { Button, Form } from "react-bootstrap";
import { logout } from "../../../../auth/infrastructure/slices/authSlice/authSlice";
import { Content } from "../../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../../general/modules/commons/content/contentHeader";
import { BreadcrumbItem } from "react-bootstrap";
import { EntitySelectField } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { useEffect, useState } from "react";
import {
  RelationModel,
  relationSchema,
} from "../../../../relation/domain/models/RelationModel";
import { DefaultRouteFormRelationEntitySelect } from "../features/DefaultRouteFormRelationEntitySelect";
import useCrud from "../../../../../hooks/useCrud";
import { z } from "zod";
import { TruckLicencePlateSelectField } from "../../../truck/modules/commons/TruckLicencePlateSelectField";

export const DefaultRouteMutatePage = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const model = useAppSelector((state) => state.crudMutate)
    .model as DefaultRouteModel;

  const [truckId, setTruckId] = useState<string>();

  const title = model.id
    ? t("defaultRoute.mutate.edit", { name: model.name })
    : t("defaultRoute.mutate.create");

  const crud = useCrudForm({
    schema: defaultRouteFormSchema,
    prefix: "defaultRoutes",
    createMutation: "upsertDefaultRoute",
    updateMutation: "upsertDefaultRoute",
    deleteMutation: "deleteDefaultRoute",
    defaultValues: {
      active: model.active == undefined ? true : model.active,
    },
    id: id ? parseInt(id) : undefined,
    editUrl: "/planning/default-route/:id/edit",
    deleteUrl: "/planning/default-routes",
    formTemplate: DefaultRouteMutateFormTemplate,
    onBeforeSubmit: (values) => {
      return {
        ...values,
        truck_id: truckId,
        id: id ? parseInt(id) : undefined,
      };
    },
  });

  useEffect(() => {
    if (model.truck) {
      setTruckId(`${model.truck.id}`);
    }

    if (model.relations) {
      crud.formLogic.setFieldValue(
        "relations",
        model.relations.map((relation) => relation.id),
      );
    }
  }, [model]);

  const relationsCrud = useCrud({
    entity: "Relation",
    prefix: "allAvailableRelationsForDefaultRoute",
    schema: z.object({
      id: z.number(),
      name: z.string(),
      status: z.boolean().default(false),
    }),
    page: 0,
    limit: 0,
  });

  useEffect(() => {
    void relationsCrud
      .getData(undefined, {
        defaultRouteId: {
          type: "ID",
          value: id,
        },
      })
      .then((data) => {
        setRelations(data as RelationModel[]);
      });
  }, []);

  const [relations, setRelations] = useState<RelationModel[]>([]);

  if (Object.keys(model).length < 1 && id) return <></>;

  if (!currentUser || currentUser.role === "dealer-employee") {
    return (
      <Layout>
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("Access denied")} />
            <p className={"mt-3"}>{t("You don't have access to this page")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );
  }

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb>
            <BreadcrumbItem href="/planning/default-routes">
              {t("Default routes")}
            </BreadcrumbItem>
            <BreadcrumbItem>{title}</BreadcrumbItem>
          </Breadcrumb>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader className={"mb-3"} title={title} />

          {crud.form}

          <Form.Group className="mb-3">
            <Form.Label>{t("Truck")}</Form.Label>
            <TruckLicencePlateSelectField
              value={truckId ?? null}
              isClearable={false}
              onChange={(option: unknown) => {
                if (!option || (Array.isArray(option) && option.length === 0)) {
                  setTruckId(undefined);
                } else {
                  // Multi-select: array, single-select: object
                  const selected = Array.isArray(option) ? option[0] : option;
                  setTruckId((selected as { value: string }).value);
                }
              }}
              isInvalid={false}
              isMulti={false}
            />
          </Form.Group>

          <crud.formLogic.Field name="relations" mode="array">
            {(field) => {
              const values = field.state.value as
                | (number | undefined)[]
                | undefined;
              return (
                <div>
                  <Form.Label>{t("Dealer")}</Form.Label>
                  <div className="d-flex gap-2 flex-column">
                    {values?.map((_, i) => {
                      return (
                        <crud.formLogic.Field key={i} name={`relations[${i}]`}>
                          {(subField) => {
                            return (
                              <div>
                                <DefaultRouteFormRelationEntitySelect
                                  subField={subField}
                                  values={values}
                                  relations={relations}
                                  removeField={() => {
                                    void field.removeValue(i);
                                  }}
                                />
                              </div>
                            );
                          }}
                        </crud.formLogic.Field>
                      );
                    })}
                  </div>
                  {values == undefined || values?.length === 0 ? (
                    <EntitySelectField
                      prefix="relations"
                      entity="Relation"
                      id={"new-relation"}
                      name={"new-relation"}
                      schema={relationSchema} // todo: make schema smaller
                      limit={100}
                      onChange={(option) => {
                        if (option === null) return;
                        const typedOption = option as { value: number };
                        if (typedOption.value === undefined) return;
                        field.pushValue(typedOption.value);
                      }}
                      isFake={true}
                      filters={{
                        searchBy: "SearchByRootRelation",
                        fields: {
                          status: {
                            equal: true,
                          },
                        },
                      }}
                    />
                  ) : (
                    <Button
                      variant="danger"
                      className="mt-4 ml-auto float-end btn-sm"
                      onClick={() => {
                        field.pushValue(undefined);
                      }}
                    >
                      {t("Add relation")}
                    </Button>
                  )}
                </div>
              );
            }}
          </crud.formLogic.Field>
        </Content>

        <Content md={4}>
          {crud.manageHeader}
          {crud.manage}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
